import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { useEffect, useState } from "react";
import { useIncidentExposure, type GasReading } from "@/api/queries/incidents";

/*
 * Series encoding: one fixed hue per gas. Colour follows the gas, never its
 * position, so a reading set without H2S doesn't repaint VOC. The hues are the
 * first four slots of the data-viz categorical palette, stepped per theme and
 * checked with its validator against this app's card surfaces (#FFFFFF light,
 * #111820 dark): every pair that sits next to each other in the legend clears
 * the colour-vision-deficiency and normal-vision floors, and every line clears
 * 3:1 contrast. Red is left out on purpose: next to orange or amber it collapses
 * for red-green colour-blind readers. The legend and tooltip name each gas, so
 * identity never rests on colour alone.
 */
const GAS_ORDER = ["CO", "VOC", "H2S", "LEL"];
const SERIES_COLOURS: Record<"light" | "dark", Record<string, string>> = {
  light: { CO: "#2a78d6", VOC: "#eb6834", H2S: "#159e6f", LEL: "#b07800" },
  dark: { CO: "#3987e5", VOC: "#d95926", H2S: "#199e70", LEL: "#c98500" },
};
const OTHER_GAS = { light: "#6b7280", dark: "#9ca3af" };
/** LEL is a percentage, not ppm: say so wherever the gas is named. */
const GAS_LABEL: Record<string, string> = { LEL: "LEL (%)" };

/** Tracks the time-of-day theme class that lib/theme.ts toggles on <html>. */
function useIsDark(): boolean {
  const read = () =>
    typeof document !== "undefined" &&
    document.documentElement.classList.contains("dark");
  const [dark, setDark] = useState(read);
  useEffect(() => {
    const observer = new MutationObserver(() => setDark(read()));
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class"],
    });
    return () => observer.disconnect();
  }, []);
  return dark;
}

type Row = { ts: string } & Record<string, number | string | null | undefined>;
type Firefighter = { id: string; label: string; peak: number };

const UNATTRIBUTED = "unattributed";

/**
 * One entry per firefighter in the reading set, highest single CO/VOC/H2S
 * reading first, so the default view is the firefighter the incident is about.
 * Each firefighter wore their own sensor, so their readings are separate
 * series: pivoting them into one row per timestamp interleaves a firefighter
 * beside the source with clean-air readings a few metres away, and the line
 * zig-zags between them every tick.
 */
function firefightersIn(
  readings: GasReading[],
  names: Map<string, string>,
): Firefighter[] {
  const byId = new Map<string, Firefighter>();
  for (const r of readings) {
    const id = r.user_id ?? UNATTRIBUTED;
    let ff = byId.get(id);
    if (!ff) {
      ff = {
        id,
        // Fallback uses the END of the id: ids created in one batch share
        // their first characters (UUIDv7 time prefix).
        label:
          names.get(id) ||
          (r.user_id ? `Firefighter …${id.slice(-6)}` : "Unattributed"),
        peak: 0,
      };
      byId.set(id, ff);
    }
    if (r.gas !== "LEL" && r.value_ppm != null) {
      ff.peak = Math.max(ff.peak, r.value_ppm);
    }
  }
  return Array.from(byId.values()).sort(
    (a, b) => b.peak - a.peak || a.label.localeCompare(b.label),
  );
}

/** Round a positive axis maximum up to 1, 2, 2.5 or 5 times a power of ten. */
function niceCeil(v: number): number {
  if (!(v > 0)) return 1;
  const p = 10 ** Math.floor(Math.log10(v));
  return [1, 2, 2.5, 5, 10].map((m) => m * p).find((c) => c >= v) ?? 10 * p;
}

export function ExposureChart({
  readings,
  tz,
  truncatedRows,
}: {
  readings: GasReading[];
  tz?: string;
  /** Set when the page cap stopped the fetch: the sensor rows that loaded. */
  truncatedRows?: number;
}) {
  const theme = useIsDark() ? "dark" : "light";
  // Names for the firefighter picker. Same query key as the page's Exposure
  // tab, so this reuses that cached request.
  const exposure = useIncidentExposure(readings[0]?.incident_id ?? undefined);
  const [picked, setPicked] = useState<string | null>(null);
  if (!readings.length) {
    return (
      <div className="flex h-72 items-center justify-center rounded-md border border-dashed text-sm text-muted-foreground">
        No gas readings recorded for this incident.
      </div>
    );
  }
  const names = new Map<string, string>(
    (exposure.data ?? []).map((e) => [
      e.user_id,
      e.user_name && e.user_badge_number
        ? `${e.user_name} · ${e.user_badge_number}`
        : e.user_name || "",
    ]),
  );
  const crew = firefightersIn(readings, names);
  const shown = crew.find((f) => f.id === picked) ?? crew[0];
  const series =
    crew.length > 1
      ? readings.filter((r) => (r.user_id ?? UNATTRIBUTED) === shown.id)
      : readings;
  // One y-scale for the whole crew, so switching firefighters compares like
  // with like: clean air stays a flat line near zero instead of its sensor
  // noise being stretched to fill the chart. LEL (%) stays out of it: a %LEL
  // reading must not set the scale of the ppm axis. Recharts still widens
  // the axis for a view whose LEL line runs above it.
  const crewMax = niceCeil(
    readings.reduce(
      (m, r) => (r.gas === "LEL" ? m : Math.max(m, r.value_ppm ?? 0)),
      0,
    ),
  );

  // Pivot to one row per timestamp, one column per gas.
  const byTs = new Map<string, Row>();
  const gases = new Set<string>();
  for (const r of series) {
    gases.add(r.gas);
    const existing = byTs.get(r.measured_at) ?? { ts: r.measured_at };
    existing[r.gas] = r.value_ppm;
    byTs.set(r.measured_at, existing);
  }
  const data = Array.from(byTs.values()).sort((a, b) =>
    String(a.ts).localeCompare(String(b.ts)),
  );
  const gasList = Array.from(gases).sort((a, b) => {
    const ia = GAS_ORDER.indexOf(a);
    const ib = GAS_ORDER.indexOf(b);
    return (ia < 0 ? GAS_ORDER.length : ia) - (ib < 0 ? GAS_ORDER.length : ib);
  });
  // Counts the firefighter on screen, not the whole crew: one reading per
  // sensor timestamp, each holding up to four gases. Gases in chart order.
  const caption = [
    `${data.length} ${data.length === 1 ? "reading" : "readings"}${
      crew.length > 1 ? " from this firefighter" : ""
    }`,
    gasList.join(", "),
  ].join(" · ");
  const timeOfDay = (v: unknown) => {
    try {
      const d = new Date(String(v));
      return tz
        ? d.toLocaleTimeString("en-CA", { timeZone: tz, hour12: false })
        : d.toLocaleTimeString("en-CA", { hour12: false });
    } catch {
      return String(v);
    }
  };
  return (
    <div className="space-y-2">
      {truncatedRows != null ? (
        <p
          role="status"
          className="rounded-md border border-warning/40 bg-warning/10 p-3 text-sm"
        >
          Showing the newest {truncatedRows.toLocaleString("en-CA")} sensor
          readings across the crew; earlier readings are not shown.
        </p>
      ) : null}
      {crew.length > 1 ? (
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <label htmlFor="gas-chart-firefighter" className="font-medium">
            Firefighter
          </label>
          <select
            id="gas-chart-firefighter"
            className="h-9 rounded-md border border-input bg-background px-2 text-sm"
            value={shown.id}
            onChange={(e) => setPicked(e.target.value)}
          >
            {crew.map((f) => (
              <option key={f.id} value={f.id}>
                {f.label}
              </option>
            ))}
          </select>
          <span className="text-xs text-muted-foreground">
            {crew.length} firefighters wore their own sensor. The list starts
            with the highest reading.
          </span>
        </div>
      ) : null}
      <div className="h-72 w-full">
        <ResponsiveContainer>
          <LineChart
            data={data}
            margin={{ top: 12, right: 24, bottom: 12, left: 0 }}
          >
            {/* Recessive grid, themed via the border token. Set as a class rather
                than the stroke prop: Recharts emits stroke as an SVG presentation
                attribute (where var() does not resolve), and a CSS declaration
                outranks a presentation attribute, so this follows light/dark. */}
            <CartesianGrid className="stroke-border" strokeDasharray="3 3" />
            <XAxis dataKey="ts" tick={{ fontSize: 11 }} tickFormatter={timeOfDay} />
            <YAxis
              domain={crew.length > 1 ? [0, crewMax] : undefined}
              tick={{ fontSize: 11 }}
              label={{
                value: "ppm",
                angle: -90,
                position: "insideLeft",
                fontSize: 11,
              }}
            />
            <Tooltip labelFormatter={timeOfDay} />
            <Legend />
            {gasList.map((gas) => (
              <Line
                key={gas}
                type="monotone"
                dataKey={gas}
                name={GAS_LABEL[gas] ?? gas}
                stroke={SERIES_COLOURS[theme][gas] ?? OTHER_GAS[theme]}
                strokeDasharray={gas in SERIES_COLOURS[theme] ? undefined : "6 4"}
                dot={false}
                strokeWidth={2}
                isAnimationActive={false}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>
      <p className="text-xs text-muted-foreground">{caption}</p>
    </div>
  );
}
